/**
 * Condition / Grade picker — a two-step control.
 *
 * The user first chooses a grader (Raw / PSA / BGS / CGC); the second dropdown
 * then shows only that grader's grades, so the full ~30-value combined list is
 * never displayed at once. The Grade <select> carries name="Condition/Grade"
 * so readForm() reads it directly; the grader <select> is a UI helper (no name).
 */
import { escapeHtml } from "../lib/dom";

export const GRADERS = ["Raw", "PSA", "BGS", "CGC"] as const;

// PSA: whole 1–10. BGS/CGC: 1–10 with half grades from 7 up. Raw: playability.
export const GRADE_SETS: Record<string, string[]> = {
  Raw: ["Near Mint", "Mint", "Lightly Played", "Moderately Played", "Heavily Played", "Damaged", "Other"],
  PSA: ["PSA 10", "PSA 9", "PSA 8", "PSA 7", "PSA 6", "PSA 5", "PSA 4", "PSA 3", "PSA 2", "PSA 1"],
  BGS: ["BGS Black Label 10", "BGS 10", "BGS 9.5", "BGS 9", "BGS 8.5", "BGS 8", "BGS 7.5", "BGS 7", "BGS 6", "BGS 5", "BGS 4", "BGS 3", "BGS 2", "BGS 1"],
  CGC: ["CGC Pristine 10", "CGC 10", "CGC 9.5", "CGC 9", "CGC 8.5", "CGC 8", "CGC 7.5", "CGC 7", "CGC 6", "CGC 5", "CGC 4", "CGC 3", "CGC 2", "CGC 1"],
  TAG: ["TAG Pristine 10", "TAG 10", "TAG 9.5", "TAG 9", "TAG 8.5", "TAG 8", "TAG 7.5", "TAG 7", "TAG 6", "TAG 5", "TAG 4", "TAG 3", "TAG 2", "TAG 1"],
};

/** Which grader a stored Condition/Grade value belongs to. */
export function graderOf(value: string): string {
  const v = String(value || "").trim();
  if (/^PSA(\s|$)/i.test(v)) return "PSA";
  if (/^BGS(\s|$)/i.test(v)) return "BGS";
  if (/^CGC(\s|$)/i.test(v)) return "CGC";
  return "Raw";
}

/** Grade options for a grader, preserving any existing non-standard value. */
function optionsFor(grader: string, current: string): string[] {
  const base = (GRADE_SETS[grader] || []).slice();
  if (current && graderOf(current) === grader && !base.includes(current)) base.unshift(current);
  return base;
}

function gradeOptionsHtml(grader: string, current: string): string {
  const opts = optionsFor(grader, current)
    .map(v => `<option value="${escapeHtml(v)}" ${v === current ? "selected" : ""}>${escapeHtml(v)}</option>`)
    .join("");
  return `<option value="">—</option>${opts}`;
}

/** One grid cell: a Grader select + a Grade select bound to Condition/Grade. */
export function gradePickerHtml(current: string): string {
  const cur = String(current || "");
  const grader = graderOf(cur);
  const graderOpts = GRADERS
    .map(g => `<option value="${g}" ${g === grader ? "selected" : ""}>${g === "Raw" ? "Raw / Ungraded" : g}</option>`)
    .join("");
  return `
    <div>
      <label class="label">Condition / Grade</label>
      <div class="flex gap-2">
        <select class="select" data-grader style="flex:0 0 42%">${graderOpts}</select>
        <select class="select" name="Condition/Grade" data-grade style="flex:1 1 auto">${gradeOptionsHtml(grader, cur)}</select>
      </div>
      <div class="text-xs text-[var(--muted)] mt-1">Pick the grader first, then its grade. Use "Raw / Ungraded" for unslabbed cards.</div>
    </div>`;
}

/** Repopulate the Grade select whenever the Grader changes. */
export function wireGradePicker(root: ParentNode = document): void {
  const grader = root.querySelector<HTMLSelectElement>("[data-grader]");
  const grade = root.querySelector<HTMLSelectElement>("select[data-grade]");
  if (!grader || !grade) return;
  grader.addEventListener("change", () => {
    grade.innerHTML = gradeOptionsHtml(grader.value, "");
    grade.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

/** Preset service providers offered by the Grading/Repair form. */
export const GRADING_PROVIDERS = ["PSA", "BGS", "CGC", "TAG", "Other"] as const;

/** Grade options for a grading provider (PSA / BGS / CGC / TAG); [] if unknown. */
export function gradesForProvider(provider: string): string[] {
  const p = String(provider || "").trim().toUpperCase();
  if (p.startsWith("PSA")) return GRADE_SETS.PSA;
  if (p.startsWith("BGS")) return GRADE_SETS.BGS;
  if (p.startsWith("CGC")) return GRADE_SETS.CGC;
  if (p.startsWith("TAG")) return GRADE_SETS.TAG;
  return [];
}
